import React from "react";
import {ToggleButton, ToggleButtonGroup} from "@mui/material";
import {FindInPageOutlined, ReportProblemOutlined, SavingsOutlined} from "@mui/icons-material";
import type {SitKpiPill} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {SitKpiHeader} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import type {ContractDetail, OpportunityKind} from "../contractExplorerTypes.ts";
import {KIND_LABEL} from "../contractExplorerTypes.ts";
import {formatMoney, opportunityTotals} from "../contractExplorerFormat.ts";
import {ChecksSection} from "./ChecksSection.tsx";
import {OpportunitiesGrid} from "./OpportunitiesGrid.tsx";
import {OpportunityDetail} from "./OpportunityDetail.tsx";
import {Section} from "./Section.tsx";

type Props = {bagId: number; contract: ContractDetail};

/**
 * The leakages cat-contract-extract's specialist agents found against the
 * bundle: the totals, the findings by kind (a row opens one with its analysis,
 * recommended action and evidence), and what each specialist made of it.
 */
export const OpportunitiesTab: React.FC<Props> = ({bagId, contract}) => {
    const [kind, setKind] = React.useState<OpportunityKind | null>(null);
    const [open, setOpen] = React.useState<number | null>(null);
    const counts = contract.opportunity_counts;
    if (!counts.length) {
        return (
            <Section title="Opportunities"
                     sub="None were imported: they come with an opportunities pipeline's export from cat-contract-extract.">
                {null}
            </Section>
        );
    }
    const totals = opportunityTotals(counts);
    const currency = contract.spend_currency;
    const kpis: SitKpiPill[] = [
        {icon: <SavingsOutlined/>, tone: 'ok', label: 'Estimated saving', value: formatMoney(totals.saving, currency),
            help: 'Over the leakages; cat-contract-extract recomputed each from the transactions and contract values it cites.'},
        {icon: <ReportProblemOutlined/>, tone: 'warn', label: 'At risk', value: formatMoney(totals.atRisk, currency),
            help: 'Spend outside the period, off contract or maverick: no saving, only an amount at risk.'},
        {icon: <FindInPageOutlined/>, tone: 'info', label: 'Findings', value: totals.findings},
    ];
    return <>
        <SitKpiHeader items={kpis}/>
        <Section title="Findings" sub="The most at stake first. Open one for its analysis, recommended action and evidence."
                 right={(
                     <ToggleButtonGroup size="small" exclusive value={kind ?? 'all'} className="ci-segmented ci-kinds"
                                        onChange={(_e, next: OpportunityKind | 'all' | null) => next && setKind(next === 'all' ? null : next)}>
                         <ToggleButton value="all">All · {totals.findings}</ToggleButton>
                         {counts.map(c => (
                             <ToggleButton key={c.kind} value={c.kind} disabled={!c.state || !c.findings}>
                                 {KIND_LABEL[c.kind]} · {c.state ? c.findings : 'not run'}
                             </ToggleButton>
                         ))}
                     </ToggleButtonGroup>
                 )}>
            <OpportunitiesGrid key={kind ?? 'all'} bagId={bagId} contractId={contract.id} kind={kind} currency={currency}
                               onOpen={setOpen}/>
        </Section>
        <ChecksSection bagId={bagId} contractId={contract.id}/>
        <OpportunityDetail bagId={bagId} contractId={contract.id} opportunityId={open} onClose={() => setOpen(null)}/>
    </>;
};
